import React, { useEffect, useState } from 'react';
import { Form, Grid, Button, Header, Dropdown } from 'semantic-ui-react';
import config from '../../../../common/repository/config';
import LocalStorage from '../../../../lib/LocalStorage';
import { toast } from '../../../common/Toast';


interface AddTemplateProps {
    onBack: () => void;
}

interface TypeOption {
    key: string;
    value: string;
    text: string;
}
interface TypeStatusOption {
    key: string;
    value: string;
    text: string;
}
const AddTemplate: React.FC<AddTemplateProps> = ({ onBack }) => {
    const [name, setName] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [typeOptions, setTypeOptions] = useState<TypeOption[]>([]);
    const [typeStatusOptions, setTypeStatusOptions] = useState<TypeStatusOption[]>([]);
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [selectedStatus,setSelectedStatus]=useState<any | null>(null);

    const [placeholders, setPlaceholders] = useState<{ key: string; text: string; value: string }[]>([]);
    const [selectedPlaceholders, setSelectedPlaceholders] = useState<string[]>(['']);

    const handleChange = (index: number, value: string) => {
        const updated = [...selectedPlaceholders];
        updated[index] = value;
        setSelectedPlaceholders(updated);
    };

    const handleAddPlaceholder = () => {
        setSelectedPlaceholders([...selectedPlaceholders, '']);
    };

    const handleSubmit = async () => {
        if (!name.trim() || !message.trim() || !selectedType || selectedPlaceholders.length === 0) {
            if (!name.trim()) {
                toast.error('Please Enter Name');
            }
            if (!selectedType) {
                toast.error('Please Enter Type');
            }
            if (selectedPlaceholders.length === 0) {
                toast.error('Please Select Placeholder');
            }
            if (!message.trim()) {
                toast.error('Please Enter Message');
            }
    
            return;
        }
    
        setLoading(true);
        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) throw new Error('Authentication token is missing.');
            const response = await fetch(`${config.apiBaseURL}/template/addTemplate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
                body: JSON.stringify({
                    name,
                    message,
                    message_subject: subject,
                    type: selectedType,
                    status:selectedStatus,
                    placeholder: selectedPlaceholders,
                }),
            });
            const result = await response.json();
            const messages = result?.message || '';
    
            // If server returns an error related to placeholders
            if (messages.toLowerCase().includes('placeholder')) {
                toast.error(messages || 'Error with placeholders');
            } else if (messages.toLowerCase().includes('already exists')) {
                toast.error(messages || 'Template already exists');
            } else {
                toast.success(messages || 'Template added successfully');
                onBack();
                setName('');
                setSubject('');
                setMessage('');
                setSelectedPlaceholders(['']);
            }
        } catch (err) {
            toast.error('Something went wrong');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };
    


    const handleGetType = async () => {
        setLoading(true);
        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) throw new Error('Authentication token is missing.');

            const response = await fetch(`${config.apiBaseURL}/template/getTemplate`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
            });

            const result = await response.json();
            const items = result?.data?.items || {};

            const formattedOptions: TypeOption[] = Object.entries(items).map(([key, value]) => ({
                key,
                value: key,
                text: value as string,
            }));

            setTypeOptions(formattedOptions);
        } catch (err) {
            toast.error('Something went wrong');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleGetPlaceholders = async () => {
        setLoading(true);
        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) throw new Error('Authentication token is missing.');

            const response = await fetch(`${config.apiBaseURL}/template/getPlaceholder`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
            });

            const result = await response.json();
            const items = result?.data?.items || {};
            const formatted = Object.entries(items).map(([key, val]) => ({
                key,
                text: val as string,
                value: key,
            }));

            setPlaceholders(formatted);
        } catch (err) {
            toast.error('Failed to fetch placeholders');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };



    const handleGetStatus = async () => {
        setLoading(true);
        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) throw new Error('Authentication token is missing.');

            const response = await fetch(`${config.apiBaseURL}/template/getTemplateStatus`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
            });

            const result = await response.json();
            const items = result?.data?.items || {};

            const formattedOptions: TypeOption[] = Object.entries(items).map(([key, value]) => ({
                key,
                value: key,
                text: value as string,
            }));

            setTypeStatusOptions(formattedOptions);
        } catch (err) {
            toast.error('Something went wrong');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleGetType();
        handleGetPlaceholders();
        handleGetStatus();
    }, []);
    const handleDelete = (indexToRemove: number) => {
        setSelectedPlaceholders(prev =>
            prev.filter((_, index) => index !== indexToRemove)
        );
    };


    return (
        <div style={{ padding: '3rem 2rem 2rem' }}>
            <Form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <Header as="h3" dividing style={{ marginBottom: '2rem' }}>
                    Add Template
                </Header>
                <Grid padded="vertically">
                    <Grid.Row columns={2} verticalAlign="middle">
                        <Grid.Column width={3}>
                            <label style={{ fontWeight: 600 }}>Name<span style={{ color: 'red' }}>*</span></label>
                        </Grid.Column>
                        <Grid.Column width={6}>
                            <Form.Input
                                placeholder='Enter template name'
                                value={name}
                                onChange={(e, data) => setName(data.value as string)}
                            />
                        </Grid.Column>

                    </Grid.Row>

                    <Grid.Row columns={2} verticalAlign="middle">
                        <Grid.Column width={3}>
                            <label style={{ fontWeight: 600 }}>Type<span style={{ color: 'red' }}>*</span></label>
                        </Grid.Column>

                        <Grid.Column width={6}>
                            <Dropdown
                                placeholder='Select Type'
                                fluid
                                selection
                                options={typeOptions}
                                value={selectedType as string}
                                onChange={(e, data) => setSelectedType(data.value as string)}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row columns={2} verticalAlign="middle">
                    <Grid.Column width={3}>
                            <label style={{ fontWeight: 600 }}>Status<span style={{ color: 'red' }}>*</span></label>
                        </Grid.Column>
                        <Grid.Column width={6}>
                            <Dropdown
                                placeholder='Select Status'
                                fluid
                                selection
                                options={typeStatusOptions}
                                value={selectedStatus as string}
                                onChange={(e, data) => setSelectedStatus(data.value as string)}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    {selectedPlaceholders.map((selected, index) => (
                        <Grid.Row key={index} verticalAlign="middle">
                            <Grid.Column width={3}>
                                <label style={{ fontWeight: 600 }}>Placeholder {index + 1}</label>
                            </Grid.Column>

                            <Grid.Column width={6}>
                                <Form.Dropdown
                                    placeholder="Select Placeholder"
                                    fluid
                                    selection
                                    options={placeholders}
                                    value={selected}
                                    onChange={(e, { value }) => handleChange(index, value as string)}
                                />
                            </Grid.Column>

                            <Grid.Column width={1}>
                                <Button
                                    icon="close"
                                    color="red"
                                    size="small"
                                    onClick={() => handleDelete(index)}
                                />
                            </Grid.Column>
                        </Grid.Row>
                    ))}


                    <Grid.Row>
                        <Grid.Column width={3} />
                        <Grid.Column width={6}>
                            <Button type="button" onClick={handleAddPlaceholder} secondary>
                                Add Placeholder
                            </Button>
                        </Grid.Column>
                    </Grid.Row>

                    <Grid.Row columns={2} verticalAlign="top">
                        <Grid.Column width={3}>
                            <label style={{ fontWeight: 600 }}>Subject</label>
                        </Grid.Column>
                        <Grid.Column width={6}>
                            <Form.TextArea
                                placeholder='Enter Subject'
                                value={subject}
                                onChange={(e, data) => setSubject(data.value as string)}
                                style={{ minHeight: 100 }}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row columns={2} verticalAlign="top">
                        <Grid.Column width={3}>
                            <label style={{ fontWeight: 600 }}>Message<span style={{ color: 'red' }}>*</span></label>
                        </Grid.Column>
                        <Grid.Column width={6}>
                            <Form.TextArea
                                placeholder='Enter message'
                                value={message}
                                onChange={(e, data) => setMessage(data.value as string)}
                                style={{ minHeight: 100 }}
                            />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                        <Grid.Column width={3} />
                        <Grid.Column width={6}>
                            <Button type='submit' primary loading={loading}>
                                Submit
                            </Button>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            </Form>
        </div>
    );
};

export default AddTemplate;
